import React from 'react';
import Card from 'react-bootstrap/Card';
import { createEmptyRecord, RESPONSE_VALUES } from './ConsolidatedFamilyHistoryTab';

const normalizeValue = (value) => {
    if (value === RESPONSE_VALUES.NA) return 'N/A';
    if (value === RESPONSE_VALUES.UNKNOWN) return 'Unknown';
    return value;
};

const isChecked = (uiValue, storedValue) => {
    if (uiValue === RESPONSE_VALUES.UNKNOWN && storedValue === 'Unknown') return true;
    if (uiValue === RESPONSE_VALUES.NA && storedValue === 'N/A') return true;
    return storedValue === uiValue;
};

const FamilyMemberSection = ({ historyItem, label, memberData, onFieldChange, disabled }) => {
    const handleChange = (field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleValueChange = (value) => {
        const normalized = normalizeValue(value);
        if (normalized === RESPONSE_VALUES.NO || normalized === 'Unknown') {
            onFieldChange(historyItem, {
                value: normalized,
                ageAtDiagnosis: '',
                notes: '',
                flagYN: 0
            });
        } else {
            onFieldChange(historyItem, { value: normalized });
        }
    };

    const handleFlagToggle = () => {
        if (memberData?.value === RESPONSE_VALUES.YES && !disabled) {
            handleChange('flagYN', memberData.flagYN === 1 ? 0 : 1);
        }
    };

    return (
        <div className="family-member-section mb-3">
            <label className="form-label">
                {label}
                {memberData?.value === RESPONSE_VALUES.YES && (
                    <i
                        className={`fas fa-flag ml-2 ${memberData.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                        onClick={handleFlagToggle}
                        role={disabled ? undefined : "button"}
                        tabIndex={disabled ? undefined : 0}
                        title={memberData.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                    />
                )}
            </label>

            <div className="mb-2">
                {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.UNKNOWN].map((value) => (
                    <div key={value} className="custom-control custom-radio custom-control-inline">
                        <input
                            type="radio"
                            id={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                            name={historyItem}
                            className="custom-control-input"
                            value={value}
                            onChange={(e) => handleValueChange(e.target.value)}
                            checked={isChecked(value, memberData?.value)}
                            disabled={disabled}
                        />
                        <label className="custom-control-label" htmlFor={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                            {value}
                        </label>
                    </div>
                ))}
            </div>

            {memberData?.value === RESPONSE_VALUES.YES && (
                <div className="row">
                    <div className="col-3">
                        <label className="form-label">Age at Diagnosis</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            value={memberData?.ageAtDiagnosis || ''}
                            onChange={(e) => handleChange('ageAtDiagnosis', e.target.value)}
                            min="0"
                            max="120"
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-9">
                        <label className="form-label">Notes</label>
                        <input
                            type="text"
                            className="form-control-custom"
                            value={memberData?.notes || ''}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const FamilyHistoryCard = ({ medicalHistory, onFieldChange, onMultipleFieldChanges, disabled = false }) => {
    const handleMainQuestionChange = (value) => {
        const normalized = normalizeValue(value);
        onFieldChange('FamilyHistoryOfBreastCancer', { value: normalized });
        if (normalized === RESPONSE_VALUES.NO || normalized === 'N/A') {
            onMultipleFieldChanges({
                MotherHadBreastCancer: createEmptyRecord('MotherHadBreastCancer'),
                SisterHadBreastCancer: createEmptyRecord('SisterHadBreastCancer'),
                DaughterHadBreastCancer: createEmptyRecord('DaughterHadBreastCancer'),
                OtherHadBreastCancer: createEmptyRecord('OtherHadBreastCancer')
            });
        }
    };

    const showFamilyMembers = medicalHistory.FamilyHistoryOfBreastCancer?.value === RESPONSE_VALUES.YES;

    return (
        <Card className="pinkCard mb-3">
            <Card.Header>
                <span>FAMILY HISTORY</span>
            </Card.Header>
            <Card.Body>
                <h4><strong><em>Answer the following</em></strong></h4>

                <div className="form-group">
                    <label className="form-label">
                        Does the patient have a family history of breast cancer? 
                        <span className="color-pink"> *</span>
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.NA].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`FamilyHistoryOfBreastCancer-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="FamilyHistoryOfBreastCancer"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleMainQuestionChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.FamilyHistoryOfBreastCancer?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`FamilyHistoryOfBreastCancer-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {showFamilyMembers && (
                    <div className="mt-4">
                        <FamilyMemberSection
                            historyItem="MotherHadBreastCancer"
                            label="Mother"
                            memberData={medicalHistory.MotherHadBreastCancer}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <FamilyMemberSection
                            historyItem="SisterHadBreastCancer"
                            label="Sister"
                            memberData={medicalHistory.SisterHadBreastCancer}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <FamilyMemberSection
                            historyItem="DaughterHadBreastCancer"
                            label="Daughter"
                            memberData={medicalHistory.DaughterHadBreastCancer}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <FamilyMemberSection
                            historyItem="OtherHadBreastCancer"
                            label="Other"
                            memberData={medicalHistory.OtherHadBreastCancer}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default FamilyHistoryCard;